import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box, HStack, Text, Button, Spinner, Badge, Alert, AlertIcon,
  Table, Thead, Tbody, Tr, Th, Td, useColorModeValue,
} from '@chakra-ui/react';
import PhoneStatusBadge from './sms/PhoneStatusBadge';
import { getSupabase } from './supabaseClient';
import { loadRecentImport } from '../utils/patientImport/recentImport';
import {
  isCutoverEffective, startPatientTableFromRds, servePatientTableFromRds,
} from '../utils/rdsCutover';

// RDS cutover flag / dashboard pilot for this view (utils/rdsCutover.js,
// TEST_INTEGRATION_PLAN.md §6, Phase 4 site 3).
const TABLE_CALL_SITE = 'patient_table';

// Columns shown first, in this order, when present. Everything else on the row
// is appended generically (in first-seen order) so dynamically-added import
// columns (address, birth_dt, …) still surface.
const CORE_COLUMNS = ['ehr_id', 'patient_name', 'email', 'phone_numbers', 'visit_dates', 'created_at'];

// Friendly header labels; anything not listed is humanized from its snake_case name.
const COLUMN_LABELS = {
  ehr_id: 'EHR ID',
  patient_name: 'Patient name',
  email: 'Email',
  phone_numbers: 'Phone numbers',
  visit_dates: 'Visit dates',
  created_at: 'Created',
  updated_at: 'Updated',
  source_record_ids: 'Source records',
};

function columnLabel(col) {
  if (COLUMN_LABELS[col]) return COLUMN_LABELS[col];
  return String(col).replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

// Ordered column list = core columns that exist on the data, then every other
// column in the order it was first seen across the rows.
function computeColumns(rows) {
  const seen = [];
  const seenSet = new Set();
  rows.forEach((r) => {
    Object.keys(r || {}).forEach((k) => {
      if (!seenSet.has(k)) { seenSet.add(k); seen.push(k); }
    });
  });
  const core = CORE_COLUMNS.filter((c) => seenSet.has(c));
  const extras = seen.filter((c) => !CORE_COLUMNS.includes(c));
  return [...core, ...extras];
}

// Render a stored cell value the way it reads best: jsonb arrays as a comma
// list, timestamps as a local date-time, other objects as JSON, else a string.
function formatCell(col, v) {
  if (v == null) return '';
  if (Array.isArray(v)) return v.join(', ');
  if (typeof v === 'object') return JSON.stringify(v);
  if ((col === 'created_at' || col === 'updated_at') && typeof v === 'string') {
    const d = new Date(v);
    if (!Number.isNaN(d.getTime())) return d.toLocaleString();
  }
  return String(v);
}

// Coarse "x ago" for the last-import note. nowMs injectable for predictability.
function relativeTime(fromMs, nowMs = Date.now()) {
  const diff = Math.max(0, nowMs - fromMs);
  const sec = Math.round(diff / 1000);
  if (sec < 45) return 'just now';
  const min = Math.round(sec / 60);
  if (min < 60) return `${min} minute${min === 1 ? '' : 's'} ago`;
  const hr = Math.round(min / 60);
  if (hr < 24) return `${hr} hour${hr === 1 ? '' : 's'} ago`;
  const day = Math.round(hr / 24);
  return `${day} day${day === 1 ? '' : 's'} ago`;
}

/**
 * Displays the current schema's `patients_with_ehrid`, newest first, and
 * highlights the rows written by the user's most recent import.
 *
 * @param {object} props
 * @param {string} props.schema     Tenant schema (used to look up the recent-import
 *                                  highlight set; the fetched client is already schema-bound).
 * @param {() => Promise<object>} [props.getClient]  Injectable Supabase-client factory
 *                                  (defaults to the real schema-bound getSupabase) for testability.
 * @param {string} [props.maxTableHeight]  Max height of the scrollable table box (used only when
 *                                  NOT in fill mode) so it scrolls internally instead of growing the page.
 * @param {boolean} [props.fill]    When true, the root + table box flex-grow to fill a bounded parent
 *                                  (e.g. a fixed-height modal), making the table box the sole scroll region.
 */
export default function PatientTableViewer({ schema, getClient = getSupabase, maxTableHeight = 'calc(100vh - 320px)', fill = false }) {
  const [rows, setRows] = useState([]);
  const [recent, setRecent] = useState(null); // { at, keys:Set } | null
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshCount, setRefreshCount] = useState(0);

  const highlightBg = useColorModeValue('yellow.100', 'yellow.800');
  const noteColor = useColorModeValue('yellow.700', 'yellow.200');
  const mutedColor = useColorModeValue('gray.600', 'gray.400');
  const headerBg = useColorModeValue('gray.50', 'gray.700'); // opaque bg so the sticky header covers scrolling rows

  useEffect(() => {
    let cancelled = false;
    const abort = new AbortController();
    (async () => {
      setLoading(true);
      setError(null);
      try {
        // Phase 4 cutover, site 3 (utils/rdsCutover.js `patient_table`):
        // when both switches are on, RDS's answer is what the table shows
        // while the Supabase read below still runs as the parity oracle
        // and the fallback. Off (the default) this is byte-for-byte the
        // old behaviour. The RDS query starts before the Supabase read so
        // the two round trips overlap.
        const cutoverOn = await isCutoverEffective(TABLE_CALL_SITE);
        if (cancelled) return;
        const rdsStarted = cutoverOn
          ? startPatientTableFromRds(schema, { signal: abort.signal })
          : null;
        const client = await getClient();
        const { data, error: qErr } = await client
          .from('patients_with_ehrid')
          .select('*')
          .order('created_at', { ascending: false });
        if (cancelled) return;
        if (qErr) throw qErr;
        let nextRows = Array.isArray(data) ? data : [];
        if (rdsStarted) {
          const served = await servePatientTableFromRds(schema, {
            supabaseRows: nextRows, started: rdsStarted,
          });
          if (cancelled) return;
          nextRows = served.rows;
          if (served.truncated) {
            console.warn(`[PatientTableViewer] schema "${schema}" hit RDS's row cap; some patients may be missing.`);
          }
        }
        setRows(nextRows);
        setRecent(loadRecentImport(schema));
      } catch (err) {
        if (cancelled) return;
        setError(err?.message || 'Could not load patients.');
        setRows([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; abort.abort(); };
    // getClient intentionally omitted so an inline prop default doesn't refetch every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [schema, refreshCount]);

  const refresh = useCallback(() => setRefreshCount((c) => c + 1), []);

  const columns = useMemo(() => computeColumns(rows), [rows]);
  const recentKeys = recent ? recent.keys : null;
  const isRowNew = useCallback((row) => {
    if (!recentKeys || row == null || row.ehr_id == null) return false;
    return recentKeys.has(String(row.ehr_id));
  }, [recentKeys]);
  const highlightCount = useMemo(
    () => (recentKeys ? rows.filter(isRowNew).length : 0),
    [rows, recentKeys, isRowNew],
  );
  const firstColumn = columns[0];

  let content;
  if (error) {
    content = (
      <Alert status="error" borderRadius="md">
        <AlertIcon />
        <Box>
          <Text fontWeight="semibold">Couldn’t load patients.</Text>
          <Text fontSize="sm">{error}</Text>
        </Box>
      </Alert>
    );
  } else if (loading && rows.length === 0) {
    content = (
      <Box py={10} textAlign="center">
        <Spinner size="lg" />
        <Text mt={3} color={mutedColor}>Loading patients…</Text>
      </Box>
    );
  } else if (rows.length === 0) {
    content = (
      <Box py={10} textAlign="center" borderWidth="1px" borderRadius="md" borderStyle="dashed">
        <Text fontWeight="medium">No patients yet</Text>
        <Text fontSize="sm" color={mutedColor} mt={1}>Imported patients will appear here.</Text>
      </Box>
    );
  } else {
    content = (
      <Box
        overflowX="auto"
        overflowY="auto"
        maxH={fill ? undefined : maxTableHeight}
        flex={fill ? '1' : undefined}
        minH={fill ? 0 : undefined}
        borderWidth="1px"
        borderRadius="md"
      >
        <Table size="sm">
          <Thead>
            <Tr>
              {columns.map((c) => (
                <Th key={c} whiteSpace="nowrap" position="sticky" top={0} zIndex={1} bg={headerBg}>{columnLabel(c)}</Th>
              ))}
            </Tr>
          </Thead>
          <Tbody>
            {rows.map((row, i) => {
              const isNew = isRowNew(row);
              const key = row.ehr_id != null ? String(row.ehr_id) : `row-${i}`;
              return (
                <Tr key={key} bg={isNew ? highlightBg : undefined}>
                  {columns.map((c) => {
                    const text = formatCell(c, row[c]);
                    return (
                      <Td key={c} whiteSpace="nowrap" maxW="280px" isTruncated title={text}>
                        {c === firstColumn && isNew && (
                          <Badge colorScheme="yellow" mr={2}>new</Badge>
                        )}
                        {text || <Text as="span" color="gray.400">—</Text>}
                        {c === 'phone_numbers' &&
                          [].concat(row[c] || []).filter(Boolean).map((p) => (
                            <PhoneStatusBadge key={p} phone={p} compact ml={1} />
                          ))}
                      </Td>
                    );
                  })}
                </Tr>
              );
            })}
          </Tbody>
        </Table>
      </Box>
    );
  }

  return (
    <Box
      display={fill ? 'flex' : undefined}
      flexDirection={fill ? 'column' : undefined}
      flex={fill ? '1' : undefined}
      minH={fill ? 0 : undefined}
    >
      <HStack justify="space-between" align="flex-start" wrap="wrap" gap={2} mb={3} flexShrink={0}>
        <Box>
          <Text fontWeight="semibold">
            Patients{rows.length ? ` (${rows.length})` : ''}
          </Text>
          {highlightCount > 0 && recent && (
            <Text fontSize="sm" color={noteColor}>
              {highlightCount} row{highlightCount === 1 ? '' : 's'} highlighted from your last import ({relativeTime(recent.at)})
            </Text>
          )}
        </Box>
        <Button size="sm" onClick={refresh} isLoading={loading} loadingText="Refreshing">
          Refresh
        </Button>
      </HStack>
      {content}
    </Box>
  );
}
